import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function RegistrationModal({ isOpen, onClose }: RegistrationModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    specialty: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.phone.trim()) {
      alert('Пожалуйста, заполните обязательные поля: ФИО и Телефон');
      return;
    }

    if (!agreed) {
      alert('Пожалуйста, дайте согласие на обработку персональных данных');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('specialty', formData.specialty);

      const response = await fetch('https://painmed.ru/wp-json/spb-evenings/v1/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', specialty: '' });
        setAgreed(false);
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Ошибка отправки формы:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-[#F8F7F4] hover:bg-[#D8B36A]/20 rounded-full transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-[#101A2E] text-xl"></i>
            </button>

            <div className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#D8B36A] to-[#C9A459] rounded-2xl mx-auto mb-4 shadow-lg">
                  <i className="ri-user-add-line text-white text-3xl"></i>
                </div>
                <h2 className="text-3xl font-bold text-[#101A2E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Регистрация
                </h2>
                <p className="text-sm text-[#1E2430]/70">
                  Заполните форму для участия в мероприятии
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-green-800">Регистрация успешна!</p>
                    <p className="text-xs text-green-700 mt-1">Мы свяжемся с вами в ближайшее время.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <i className="ri-error-warning-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-red-800">Ошибка отправки</p>
                    <p className="text-xs text-red-700 mt-1">Попробуйте еще раз или свяжитесь с нами.</p>
                  </div>
                </div>
              )}

              <form id="registration-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#101A2E] mb-2">
                    ФИО <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#F8F7F4] border border-[#D8B36A]/20 rounded-lg text-sm text-[#101A2E] placeholder-[#1E2430]/40 focus:outline-none focus:border-[#D8B36A] focus:ring-2 focus:ring-[#D8B36A]/20 transition-all"
                    placeholder="Иванов Иван Иванович"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#101A2E] mb-2">
                    Телефон <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#F8F7F4] border border-[#D8B36A]/20 rounded-lg text-sm text-[#101A2E] placeholder-[#1E2430]/40 focus:outline-none focus:border-[#D8B36A] focus:ring-2 focus:ring-[#D8B36A]/20 transition-all"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#101A2E] mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#F8F7F4] border border-[#D8B36A]/20 rounded-lg text-sm text-[#101A2E] placeholder-[#1E2430]/40 focus:outline-none focus:border-[#D8B36A] focus:ring-2 focus:ring-[#D8B36A]/20 transition-all"
                    placeholder="example@mail.ru"
                  />
                </div>

                <div>
                  <label htmlFor="specialty" className="block text-sm font-semibold text-[#101A2E] mb-2">
                    Специальность
                  </label>
                  <input
                    type="text"
                    id="specialty"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#F8F7F4] border border-[#D8B36A]/20 rounded-lg text-sm text-[#101A2E] placeholder-[#1E2430]/40 focus:outline-none focus:border-[#D8B36A] focus:ring-2 focus:ring-[#D8B36A]/20 transition-all"
                    placeholder="Невролог, терапевт и т.д."
                  />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 text-[#D8B36A] border-[#D8B36A]/30 rounded focus:ring-[#D8B36A] cursor-pointer"
                  />
                  <label htmlFor="consent" className="text-xs text-[#1E2430]/70 leading-relaxed cursor-pointer">
                    Я даю согласие на обработку персональных данных в соответствии с <a href="#" className="text-[#D8B36A] hover:underline">152-ФЗ</a>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-4 bg-[#D8B36A] text-[#101A2E] rounded-lg font-bold text-base hover:bg-[#C9A459] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Отправка...
                    </span>
                  ) : (
                    'Забронировать место'
                  )}
                </button>
              </form>

              <p className="text-xs text-center text-[#1E2430]/60 mt-6">
                <i className="ri-lock-line mr-1"></i>
                Ваши данные защищены и не передаются третьим лицам
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}